import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useAlert } from 'react-alert';
import { personnelApi, errMsg, targetOf } from './personnelApi';

const STRONG = /^(?=.*[A-Za-z])(?=.*\d).{8,128}$/;

// Super Admin "Set temporary password" (server requires adminPasswordReset + Super Admin; audit-logged).
// `person` is the profile on screen: the confirm dialog names it and the request carries it as the target.
const PasswordResetForm = ({ person }) => {
  const { name, email } = person;
  const alert = useAlert();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const weak = password.length > 0 && !STRONG.test(password);

  const submit = (e) => {
    e.preventDefault();
    if (weak || mismatch || !password) return;
    setAsking(true);
  };

  const apply = async () => {
    setSaving(true);
    try {
      await personnelApi.resetPassword(person.kind, person.id, { password, confirmPassword: confirm }, targetOf(person));
      setAsking(false);
      alert.success(`Temporary password set for ${name || 'this user'}`);
      setPassword('');
      setConfirm('');
      setShow(false);
    } catch (err) {
      alert.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} autoComplete="off" className="p-3 mt-3" style={{ border: '1px solid #dee2e6', borderRadius: 6 }}>
      <b>Set temporary password</b>
      <div data-testid="password-target" style={{ fontSize: 13 }}>
        For <b>{name || '—'}</b>{email ? ` (${email})` : ''}. Their current password stops working. Share it securely and ask them to
        change it after signing in (Forgot Password).
      </div>
      <div className="row mt-2">
        <div className="col-md-5">
          <label className="form-label mb-0">New password</label>
          <input
            type={show ? 'text' : 'password'}
            className="form-control"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="col-md-5">
          <label className="form-label mb-0">Confirm password</label>
          <input
            type={show ? 'text' : 'password'}
            className="form-control"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button type="submit" className="edit-pill" disabled={saving || !password || weak || mismatch}>
            {saving ? 'Saving...' : 'Set'}
          </button>
        </div>
      </div>
      <div className="form-check mt-2">
        <input id="pr-show" type="checkbox" className="form-check-input" checked={show} onChange={(e) => setShow(e.target.checked)} />
        <label htmlFor="pr-show" className="form-check-label">Show password</label>
      </div>
      {weak && <small className="text-danger d-block">At least 8 characters with a letter and a number.</small>}
      {mismatch && <small className="text-danger d-block">Passwords do not match.</small>}
      <Modal show={asking} onHide={() => setAsking(false)} centered>
        <Modal.Body data-testid="confirm-password">
          <h5>
            Set password for {name} ({email})?
          </h5>
          <p className="text-muted mb-0">Only this account&apos;s password changes. The change is recorded in the audit log.</p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAsking(false)}>Cancel</button>
          <button type="button" className="btn btn-danger btn-sm" disabled={saving} onClick={apply}>Set password</button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};

export default PasswordResetForm;
